import { PermissionLevel, PrivilegesMap } from "../context/RoleContext";
import { Privilege } from "../context/constant";
import usePermissionStore from "../hooks/usePermissionStore";

type RoleControllerReturn = {
  hasPrivilege: (level: PermissionLevel, privilege: Privilege) => boolean;
  privileges: PrivilegesMap | null;
  setPrivileges: (privileges: PrivilegesMap) => void;
} & AccessReq;

type reqKey = "hasView" | "hasUpdate" | "hasNone" | "hasReadOnly" | "hasDelete";
type AccessReq = Record<reqKey, boolean>;

/**
 * Custom hook for role-based access control operations
 * Provides utility functions to check privileges and access levels
 *
 * @returns Object containing privilege checking functions
 *
 * @example
 * const { hasPrivilege, privileges, setPrivileges } = useRoleController();
 *
 * hasPrivilege("APPOINTMENT","VIEW"):boolean;
 *
 * It may vary
 * setPrivileges({
 *   APPOINTMENT: { allowed: true, access: "VIEW", module: "APPOINTMENT" },
 * });
 *
 * @author [Rajkumar Rathod]
 */
export const useRoleController = (
  _priVillage: Privilege
): RoleControllerReturn => {
  const [privileges, setPrivileges] = usePermissionStore();

  /**
   * Check if a specific privilege is allowed
   * @param privilege - The privilege to check
   * @returns boolean - true if privilege is allowed
   */
  const hasPrivilege = (
    level: PermissionLevel,
    privilege: Privilege = _priVillage
  ): boolean => {
    if (!privileges) return false;

    return !!privileges[privilege]?.[level];
  };

  /** helper fnc for req permission */
  const permit = (v: PermissionLevel) => hasPrivilege(v, _priVillage);

  const checkNonePermission = () => !permit("update") && !permit("ready_only");

  const checkReadOnlyPermission = () =>
    permit("ready_only") && !permit("update");

  /**
   * @description To simplify the req access and reduce calling check for hasPrivilege
   */
  const accessReqMapper: AccessReq = {
    hasDelete: permit("update"),
    hasView: permit("update") || permit("ready_only"),
    hasUpdate: permit("update"),
    /** this check for remove the complete section */
    hasNone: checkNonePermission(),
    hasReadOnly: checkReadOnlyPermission(),
  };

  return { hasPrivilege, ...accessReqMapper, privileges, setPrivileges };
};
